import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BellIcon, CalendarIcon, ChevronDownIcon, HomeIcon, LogOutIcon, MenuIcon } from 'lucide-react';
import type { AdminRole } from '../../types/config';
import type { AdminPage } from '../../types/navigation';
import { ADMIN_USERS } from '../../data/users';
import { useStore } from '../../contexts/StoreContext';
import { BdFlag } from '../brand/BdFlag';
import { ADMIN_MENU } from './AdminSidebar';
import { fmtDate, fmtDateTime, fmtTime, nowIso } from '../../utils/bn';
interface Props {
  role: AdminRole;
  onRole: (r: AdminRole) => void;
  onLogout: () => void;
  onExit: () => void;
  onOpenCase: (pcr: string) => void;
  allowed: AdminPage[];
  onNavigate: (p: AdminPage) => void;
}
export function AdminTopbar({
  role,
  onRole,
  onLogout,
  onExit,
  onOpenCase,
  allowed,
  onNavigate
}: Props) {
  const {
    cases
  } = useStore();
  const user = ADMIN_USERS[role];
  const [now, setNow] = useState(nowIso());
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const t = setInterval(() => setNow(nowIso()), 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const notes = useMemo(() => cases.flatMap(c => c.history.map(h => ({
    ...h,
    pcr: c.pcr
  }))).sort((a, b) => b.at.localeCompare(a.at)).slice(0, 8), [cases]);
  return <header className="sticky top-0 z-30 border-b border-line bg-white" data-id="element-23" data-mp="qw0mb4s3qz8o">
      <div className="flex items-center gap-3 px-4 lg:px-6 h-16" data-id="element-24" data-mp="mx9r53775ro7">
        <button className="lg:hidden btn btn-ghost h-9 w-9 px-0" onClick={() => setMenu(!menu)} aria-label="মেনু" data-id="element-25" data-mp="2soycesuhvdpx"><MenuIcon className="h-5 w-5" data-id="element-26" data-mp="2op6b917fk9ul" /></button>
        <button onClick={onExit} className="btn btn-ghost btn-sm" title="ডেমো হোম" data-id="element-27" data-mp="2oqhcat9hr3i0"><HomeIcon className="h-4 w-4" data-id="element-28" data-mp="rp1tqzq7xa2m" /><span className="hidden xl:inline" data-id="element-29" data-mp="wmlg6mx2kt5n">ডেমো হোম</span></button>
        <div className="hidden md:flex items-center gap-1.5 text-xs text-ink-muted" data-id="element-30" data-mp="wn3ia06ey1lh">
          <CalendarIcon className="h-4 w-4" data-id="element-31" data-mp="11kvdb32cu3tw" /> {fmtDate(now)} · {fmtTime(now)}
        </div>
        <div className="flex-1" data-id="element-32" data-mp="16lsq104li5o0" />
        <label className="hidden md:flex items-center gap-2 rounded-lg border border-warning/40 bg-warning-50 px-2 py-1 text-xs" data-id="element-33" data-mp="16iofzm4g8fpf">
          <span className="font-semibold text-warning-700 whitespace-nowrap" data-id="element-34" data-mp="1fl65qajbbqh6">ডেমো রোল</span>
          <select className="bg-transparent text-xs font-semibold text-ink focus:outline-none" value={role} onChange={e => onRole(e.target.value as AdminRole)} data-id="element-35" data-mp="1kc5yd3lefmdv">
            {(Object.keys(ADMIN_USERS) as AdminRole[]).map(r => <option key={r} value={r} data-id="element-36" data-mp="1uavht0sy1ew8">{ADMIN_USERS[r].title}</option>)}
          </select>
        </label>
        <div className="relative" ref={ref} data-id="element-37" data-mp="1yb5pchwdwdd7">
          <button className="relative btn btn-ghost h-9 w-9 px-0" onClick={() => setOpen(!open)} aria-label="বিজ্ঞপ্তি কেন্দ্র" data-id="element-38" data-mp="1yc73wwclzhzo">
            <BellIcon className="h-5 w-5" data-id="element-39" data-mp="3ese99tnyprcx" />
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-danger" data-id="element-40" data-mp="3erl95bjie03r" />
          </button>
          {open && <div className="absolute right-0 top-11 w-96 rounded-xl border border-line bg-white shadow-pop" data-id="element-41" data-mp="39ve1nebgsgjp">
              <p className="border-b border-line px-4 py-2.5 text-sm font-bold" data-id="element-42" data-mp="34wjj8dhesu04">বিজ্ঞপ্তি কেন্দ্র</p>
              <ul className="max-h-96 overflow-y-auto scroll-thin divide-y divide-line" data-id="element-43" data-mp="6ee0yyjw785z">
                {notes.map((n, i) => <li key={i} data-id="element-44" data-mp="3srdussg6hq4v" data-mpi={i}>
                    <button className="w-full px-4 py-2.5 text-left hover:bg-navy-50" onClick={() => {
                onOpenCase(n.pcr);
                setOpen(false);
              }} data-id="element-45" data-mp="3sqkxsj70uezx">
                      <p className="text-sm font-semibold text-ink" data-id="element-46" data-mp="p32hz9f98tch">{n.action}</p>
                      <p className="text-xs text-ink-muted" data-id="element-47" data-mp="p4vnumi1n9jf">{n.pcr} · {n.actor} · {fmtDateTime(n.at)}</p>
                    </button>
                  </li>)}
              </ul>
            </div>}
        </div>
        <div className="flex items-center gap-2.5 border-l border-line pl-3" data-id="element-48" data-mp="8wfqynk6j327">
          <div className="hidden sm:block text-right leading-tight" data-id="element-49" data-mp="3r1izyjd9nz7w">
            <p className="text-sm font-semibold text-ink" data-id="element-50" data-mp="3r3dixs4z9h9c">{user.name}</p>
            <p className="text-[11px] text-ink-muted" data-id="element-51" data-mp="3w1oxgg67wfxw">{user.title} · {user.unit}</p>
          </div>
          <BdFlag width={26} data-id="element-52" data-mp="nlvhc2fst6hd" />
          <button className="btn btn-ghost h-9 w-9 px-0" onClick={onLogout} title="লগআউট" aria-label="লগআউট" data-id="element-53" data-mp="nmx2izqj39ct"><LogOutIcon className="h-4 w-4" data-id="element-54" data-mp="slzw9o125t5h" /></button>
        </div>
      </div>
      {menu && <nav className="lg:hidden border-t border-line bg-white px-4 py-2 grid grid-cols-2 gap-1" data-id="element-55" data-mp="dur16zedzfky">
          {ADMIN_MENU.filter(m => allowed.includes(m.id)).map(m => <button key={m.id} className="btn btn-ghost btn-sm justify-start" onClick={() => {
        onNavigate(m.id);
        setMenu(false);
      }} data-id="element-56" data-mp="16iwl5jxm90jq"><m.icon className="h-4 w-4" data-id="element-57" data-mp="1bbpp45l9g4ur" />{m.label}</button>)}
          <label className="col-span-2 flex items-center gap-2 text-xs" data-id="element-58" data-mp="2vzs6vilu5md1">ডেমো রোল <ChevronDownIcon className="h-3 w-3" data-id="element-59" data-mp="2r1yx6flbqa80" />
            <select className="input h-8" value={role} onChange={e => onRole(e.target.value as AdminRole)} data-id="element-60" data-mp="2r17cw0x6fyx1">
              {(Object.keys(ADMIN_USERS) as AdminRole[]).map(r => <option key={r} value={r} data-id="element-61" data-mp="30ra724g1biwy">{ADMIN_USERS[r].title}</option>)}
            </select>
          </label>
        </nav>}
    </header>;
}